import { useEffect, useRef } from 'react';

import { Input } from '@/_shared/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/_shared/components/ui/radio-group';
import type { Question } from '@/_shared/types';

import { OptionDescription } from './OptionDescription';
import { OptionStat } from './OptionStat';
import { otherBlockClass } from './other-block';
import { OTHER_VALUE } from './selection';

interface Props {
  question: Question;
  value: string | null;
  onValueChange: (value: string | null) => void;
  otherText: string;
  onOtherTextChange: (text: string) => void;
  disabled?: boolean;
}

/** The controlled single-select option list (radio rows + always-on "Other" free-text), sans card/footer. */
export function SingleSelectList({
  question,
  value,
  onValueChange,
  otherText,
  onOtherTextChange,
  disabled,
}: Props): React.ReactElement {
  const isOther = value === OTHER_VALUE;
  const otherInputRef = useRef<HTMLInputElement>(null);

  // Radix selects a row as arrow-key focus passes over it, so arrowing to the bottom lands on Other.
  // Pulling focus into the text box there would strand the user outside the radiogroup with no
  // arrow-key route back to the options — so only a deliberate pick (click, Space, number key) focuses.
  const viaArrowKey = useRef(false);
  useEffect(() => {
    if (isOther && !viaArrowKey.current) otherInputRef.current?.focus();
  }, [isOther]);

  // The effect above only fires when `isOther` FLIPS. Arrowing onto Other sets it without focusing (by
  // design), and from then on a deliberate pick re-picks a row that is already checked — which Radix does
  // not report through `onValueChange`, so nothing re-runs. Focus explicitly on the gesture instead.
  //
  // Hung off the row's CLICK, never its keydown: the browser fires a button's Space activation on KEYUP,
  // so a keydown handler moves focus while the keypress is still in flight and the space character lands
  // in the box — which `onChange` reads as empty text and answers by RELEASING the row (ACT2-538).
  const focusOtherOnDeliberatePick = (): void => {
    // Radix fires a synthetic click on the row it arrows onto, so the gesture alone is not proof of
    // intent — `viaArrowKey` is what separates the two, and arrowing must still leave focus on the row.
    if (viaArrowKey.current) return;
    otherInputRef.current?.focus();
  };

  // Picking a real row abandons any free text — drop it so the input never shows an answer that
  // `buildSingleEntry` silently omits. Landing on Other focuses its box unconditionally: the guard inside
  // `focusOtherOnDeliberatePick` is the single place that decides whether the gesture was deliberate, so
  // an arrow-key pass still leaves focus on the row.
  const selectRow = (next: string): void => {
    onValueChange(next);
    if (next !== OTHER_VALUE) onOtherTextChange('');
    else focusOtherOnDeliberatePick();
  };

  const rowValues = [...question.options.map((o) => o.label), OTHER_VALUE];
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (disabled) return;
    // The Other box sits INSIDE the group (see the block below), so bail on a text target before reading
    // anything: its digits are text rather than row picks, and its arrows are caret moves rather than a
    // pass over the rows. Any other field nested here later gets the same protection for free.
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
    viaArrowKey.current = e.key.startsWith('Arrow');
    if (e.key >= '1' && e.key <= '9') {
      const idx = Number(e.key) - 1;
      // A number key is always a deliberate pick, including onto an already-checked Other — `viaArrowKey`
      // is false here because a digit never starts with "Arrow", so `selectRow` focuses the box.
      if (idx < rowValues.length) {
        // The shortcut CONSUMES its keypress. Without this, focus moves into the Other box while the
        // keydown is still in flight and the browser inserts the digit there; `onChange` reads that as a
        // real answer, so pressing "3" submits "3" as the user's free text — ACT2-538 by another key.
        e.preventDefault();
        selectRow(rowValues[idx]!);
      }
    }
  };

  return (
    <RadioGroup
      value={value ?? ''}
      onValueChange={selectRow}
      disabled={disabled}
      aria-label={question.prompt}
      onKeyDown={handleKeyDown}
      // A pointer pick is always deliberate, so clear the arrow-key flag before the click lands.
      onPointerDown={() => (viaArrowKey.current = false)}
    >
      {question.options.map((opt) => (
        <RadioGroupItem key={opt.label} value={opt.label}>
          <span className="min-w-0 flex-1">
            <span className="block font-medium">{opt.label}</span>
            {opt.description && <OptionDescription content={opt.description} />}
          </span>
          {opt.stat && <OptionStat stat={opt.stat} />}
        </RadioGroupItem>
      ))}

      {/* The row and its always-mounted box are one block, and `otherBlockClass` owns every surface the two
          share. The box lives INSIDE the group so the block can be a single element: safe, because Radix's
          roving-focus keydown sits on the ITEM and returns early unless the item is itself the target, so
          arrow keys in the box move the caret rather than the selection.

          KNOWN COST, ACCEPTED FOR NOW: `role="radiogroup"` may own only `radio` children, so the box being
          inside it is an ARIA violation (axe `aria-required-children`). It buys ONE element owning the
          block's fill and ring, and there is no way to keep both with Radix: it resolves item order via
          `querySelectorAll` on the group element, so the Other radio must be a DOM descendant of the
          radiogroup, and any wrapper holding both the radio and the box therefore sits inside it too.
          The real fix is to drop Radix for this one control and use a native `<fieldset>` +
          `<input type="radio">` — native radios group by `name`, so no `role="radiogroup"` is involved and
          a fieldset may hold anything (this is the GOV.UK conditional-reveal pattern). Deferred: it means
          reproducing `RadioGroupItem`'s states on native inputs, which is its own change. */}
      <div className={otherBlockClass(isOther, Boolean(disabled))}>
        <RadioGroupItem
          value={OTHER_VALUE}
          data-other-row
          // The block draws the fill, the ring AND the disabled fade for the row and the box together, so
          // the row must draw none of them on its own — each would stop at the seam, and a second
          // `opacity-50` over the block's would compound to 25%.
          className="focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-100 data-[state=checked]:bg-transparent data-[state=unchecked]:hover:bg-transparent"
          onClick={focusOtherOnDeliberatePick}
        >
          <span className="flex-1 font-medium">Other</span>
        </RadioGroupItem>

        {/* Always mounted — typing auto-selects Other. */}
        <div className="pr-2.5 pb-2 pl-9.5">
          <Input
            ref={otherInputRef}
            value={otherText}
            onChange={(e) => {
              const text = e.target.value;
              onOtherTextChange(text);
              // Real text claims the Other row and clearing it releases the row again, so a stray
              // keystroke can never strand the card on an empty Other with the prior pick discarded.
              const hasText = text.trim().length > 0;
              if (hasText !== isOther) onValueChange(hasText ? OTHER_VALUE : null);
            }}
            placeholder="Type your own answer here"
            disabled={disabled}
            aria-label="Your answer"
            maxLength={10000}
          />
        </div>
      </div>
    </RadioGroup>
  );
}
